/**
 * Safe localStorage access (#1761, #2028).
 *
 * `localStorage` is not guaranteed to exist or to work. Safari private
 * browsing, cookie/storage blockers, locked-down corporate profiles and some
 * embedded webviews throw a `SecurityError` — and they throw on the
 * `window.localStorage` property read itself, not just on the method call, so
 * optional chaining (`window.localStorage?.getItem`) is *not* a guard.
 *
 * A bare call in a component crashes that render. Where the call sits in a
 * `useState` initializer (Home, Stats) the whole page white-screens, and Home
 * is the landing route. Route every read/write through these wrappers so a
 * blocked store degrades to "no persisted preference" instead of a crash.
 *
 * This module is the single source for storage access — no other frontend file
 * should touch `localStorage` directly.
 */

/**
 * Read a key. Returns `fallback` (default `null`) when the key is absent or
 * storage is unavailable — the two cases are deliberately indistinguishable,
 * since both mean "no persisted preference".
 */
export function safeLocalStorageGet(key, fallback = null) {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

/**
 * Write a key. Best-effort: a blocked or full store just means the preference
 * doesn't survive a reload.
 */
export function safeLocalStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, quota, blocker) — nothing to do */
  }
}

/** Remove a key. Same best-effort contract as {@link safeLocalStorageSet}. */
export function safeLocalStorageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — nothing to do */
  }
}
